/**
 * Wind as the trees feel it.
 *
 * Open-Meteo gives two numbers: the 10-minute mean wind and the peak gust of
 * the last hour. Real wind moves between them in gusts, which are patches of
 * faster air carried along with the flow. You can watch one cross a field of
 * grass or a forest canopy. This module is the CPU half of that model:
 *
 * - **Smoothing.** A new observation eases in over half a minute or so, so
 *   the wind builds or dies away rather than switching.
 * - **Drift.** The gust pattern is "frozen turbulence" (Taylor's hypothesis):
 *   a fixed noise field moved downwind at the mean speed. `drift` is how far
 *   it has travelled. The shader samples it at each tree.
 * - **Response.** A tree's tip deflection grows with the square of the wind
 *   speed, because drag goes as v², and levels off as the tree streamlines and
 *   stiffens. `bendFraction` gives that curve, and the GLSL in
 *   `three/flora.ts` uses the same one.
 *
 * Scene axes: +x east, +z south. So a wind *from* the west blows toward +x.
 */

import type { Weather } from './weather.js';

/** Seconds for the smoothed wind to cover ~63% of a change. */
const EASE = 20;

/** Largest tip deflection, as a share of tree height, in a hurricane. */
export const MAX_BEND = 0.2;
/** Wind speed (m/s) at which the bend is ~63% of its maximum. */
export const BEND_SPEED = 18;

/**
 * Horizontal tip deflection as a share of height, for a tree of average
 * flexibility in a wind of `v` m/s. It rises as v² through a breeze and
 * saturates in a storm. At 5 m/s a tree barely leans (~1.5%), at 12 m/s it
 * visibly bows (~7%), and at 25 m/s it is bent hard over (~17%).
 */
export function bendFraction(v: number): number {
  const r = v / BEND_SPEED;
  return MAX_BEND * (1 - Math.exp(-r * r));
}

/**
 * Natural sway frequency (Hz) of a tree `height` metres tall. Measured trees
 * sit around 0.2–0.5 Hz for 10–30 m, and shrubs and grass are quicker.
 */
export function swayFrequency(height: number): number {
  return 1.6 / Math.sqrt(Math.max(0.3, height));
}

/** Unit vector (scene x, z) the air moves toward, for a wind blowing *from* `fromDeg`. */
export function windDirection(fromDeg: number): { x: number; z: number } {
  const toward = ((fromDeg + 180) * Math.PI) / 180;
  return { x: Math.sin(toward), z: -Math.cos(toward) };
}

export class WindState {
  /** Smoothed mean wind, scene m/s. */
  x = 0;
  z = 0;
  /** Smoothed peak gust speed, m/s. */
  gust = 0;
  /** How far the gust pattern has been carried downwind, metres. */
  readonly drift = { x: 0, z: 0 };
  /** Seconds since start, for oscillation. */
  time = 0;
  private started = false;

  get speed(): number {
    return Math.hypot(this.x, this.z);
  }

  /** Unit direction the air moves toward. Holds its last value in a calm. */
  readonly dir = { x: 1, z: 0 };

  update(dt: number, weather: Weather): void {
    const d = windDirection(weather.windFrom);
    const tx = d.x * weather.wind;
    const tz = d.z * weather.wind;
    const tg = Math.max(weather.wind, weather.gusts);
    // The first reading is taken as-is. Starting from a calm and blowing up
    // over half a minute would be a gust the weather never had.
    const k = this.started ? 1 - Math.exp(-dt / EASE) : 1;
    this.started = true;
    this.x += (tx - this.x) * k;
    this.z += (tz - this.z) * k;
    this.gust += (tg - this.gust) * k;

    const s = this.speed;
    if (s > 0.05) {
      this.dir.x = this.x / s;
      this.dir.z = this.z / s;
    }
    this.drift.x += this.x * dt;
    this.drift.z += this.z * dt;
    this.time += dt;
  }
}

/** Eight-point compass name for a bearing in degrees, as in "from the SW". */
export function compass(deg: number): string {
  const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return points[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
}
